import { ImagePlus, Images, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState, type ChangeEvent } from 'react';

interface ProfileScreenshotsProps {
  profileName: string;
  creator: string;
  notify: (message: string, tone?: 'success' | 'error') => void;
}

interface DraftScreenshot {
  id: string;
  fileName: string;
  url: string;
}

const MAX_SCREENSHOTS = 6;
const MAX_SCREENSHOT_SIZE = 8 * 1024 * 1024;
const ACCEPTED_SCREENSHOT_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

export function ProfileScreenshots({ profileName, creator, notify }: ProfileScreenshotsProps) {
  const [screenshots, setScreenshots] = useState<DraftScreenshot[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const objectUrls = useRef(new Set<string>());
  const nextScreenshotId = useRef(0);

  useEffect(() => () => {
    objectUrls.current.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  function attachScreenshots(event: ChangeEvent<HTMLInputElement>): void {
    const selectedFiles = Array.from(event.currentTarget.files ?? []);
    event.currentTarget.value = '';
    if (!selectedFiles.length) return;

    const validFiles = selectedFiles.filter(
      (file) => ACCEPTED_SCREENSHOT_TYPES.has(file.type) && file.size <= MAX_SCREENSHOT_SIZE,
    );
    if (validFiles.length !== selectedFiles.length) {
      notify('Screenshots must be PNG, JPEG, or WebP files no larger than 8 MB.', 'error');
    }

    const remainingSlots = MAX_SCREENSHOTS - screenshots.length;
    if (remainingSlots <= 0) {
      notify(`A profile can have up to ${MAX_SCREENSHOTS} screenshots.`, 'error');
      return;
    }

    const acceptedFiles = validFiles.slice(0, remainingSlots);
    if (validFiles.length > remainingSlots) {
      notify(`Only the first ${remainingSlots} selected screenshot${remainingSlots === 1 ? '' : 's'} could be added.`, 'error');
    }
    if (!acceptedFiles.length) return;

    const added = acceptedFiles.map((file, index) => {
      const url = URL.createObjectURL(file);
      objectUrls.current.add(url);
      return {
        id: `${file.name}-${file.lastModified}-${file.size}-${nextScreenshotId.current++}-${index}`,
        fileName: file.name,
        url,
      };
    });
    setScreenshots((current) => [...current, ...added]);
    notify(`${added.length} screenshot${added.length === 1 ? '' : 's'} added to this local preview.`);
  }

  function removeScreenshot(id: string): void {
    setScreenshots((current) => {
      const screenshot = current.find((item) => item.id === id);
      if (screenshot) {
        URL.revokeObjectURL(screenshot.url);
        objectUrls.current.delete(screenshot.url);
      }
      return current.filter((item) => item.id !== id);
    });
  }

  return (
    <section className="sidebar-card screenshot-section sidebar-screenshot-section">
      <div className="section-heading screenshot-heading">
        <div>
          <h2>Screenshots</h2>
          <p>Show how this profile looks in action.</p>
        </div>
        <input
          ref={inputRef}
          className="sr-only"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          aria-label={`Choose screenshots for ${profileName}`}
          onChange={attachScreenshots}
        />
        <button
          className="button secondary"
          type="button"
          disabled={screenshots.length >= MAX_SCREENSHOTS}
          onClick={() => inputRef.current?.click()}
        >
          <ImagePlus size={17} />Add Screenshots
        </button>
      </div>

      {screenshots.length > 0 ? (
        <div className="profile-screenshot-grid">
          {screenshots.map((screenshot, index) => (
            <figure className="profile-screenshot" key={screenshot.id}>
              <div className="profile-screenshot-image">
                <img src={screenshot.url} alt={`Screenshot ${index + 1} showing ${profileName} in action`} />
              </div>
              <figcaption>
                <span title={screenshot.fileName}>{screenshot.fileName}</span>
                <button
                  className="icon-button subtle"
                  type="button"
                  aria-label={`Remove ${screenshot.fileName}`}
                  onClick={() => removeScreenshot(screenshot.id)}
                >
                  <Trash2 size={16} />
                </button>
              </figcaption>
            </figure>
          ))}
        </div>
      ) : (
        <div className="screenshot-empty">
          <Images size={34} />
          <div>
            <h3>No screenshots provided</h3>
            <p>{creator} can attach up to six images to demonstrate this profile.</p>
          </div>
        </div>
      )}
    </section>
  );
}
